import axios from "axios";

// Konfiguration
const API_CONFIG = {
    BASE_URL: process.env.REACT_APP_API_BASE_URL || 'https://bewerber.volleregale.de/api',
  TIMEOUT: {
    AUTH: 30000,
    DEFAULT: 30000
  },
  TOKEN_HEADER: "Authorization",
  STORAGE_KEY: "user"
};

// Token-Management-Status
let isRefreshing = false;
let refreshQueue = [];
let refreshPromise = null;

// Generische Konfiguration für alle Instanzen
const createBaseConfig = (path, timeout) => ({
  baseURL: `${API_CONFIG.BASE_URL}/${path}`,
  timeout: timeout,
  headers: {
    "Content-type": "application/json"
  }
});

// Instanzen erstellen
const authInstance = axios.create(createBaseConfig("auth", API_CONFIG.TIMEOUT.AUTH));
const bewerberInstance = axios.create(createBaseConfig("bewerber", API_CONFIG.TIMEOUT.DEFAULT));
const jobsInstance = axios.create(createBaseConfig("jobs", API_CONFIG.TIMEOUT.DEFAULT));
const flyerInstance = axios.create(createBaseConfig("flyer", API_CONFIG.TIMEOUT.DEFAULT)); // Neue Flyer-Instanz
const plakatInstance = axios.create(createBaseConfig("plakat", API_CONFIG.TIMEOUT.DEFAULT)); // Alte Instanz für Migration
const userInstance = axios.create(createBaseConfig("users", API_CONFIG.TIMEOUT.DEFAULT));
const umantisInstance = axios.create(createBaseConfig("umantis", API_CONFIG.TIMEOUT.DEFAULT));
const contractsInstance = axios.create(createBaseConfig("contracts", API_CONFIG.TIMEOUT.DEFAULT));

// Hilfsfunktionen
const getUserFromStorage = () => {
  try {
    const userString = localStorage.getItem(API_CONFIG.STORAGE_KEY);
    if (!userString) return null;

    const user = JSON.parse(userString);
    return user && user.refreshToken && user.accessToken ? user : null;
  } catch (error) {
    console.error("Fehler beim Parsen der Benutzerdaten:", error);
    return null;
  }
};

const isTokenValid = (token) => {
  if (!token) return false;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    const tokenPayload = JSON.parse(atob(parts[1]));
    const now = Math.ceil(Date.now() / 1000);
    const timeBuffer = 30; // 30 Sekunden Puffer für Netzwerkverzögerungen

    return tokenPayload.exp > (now + timeBuffer);
  } catch (error) {
    console.error("Fehler beim Validieren des Tokens:", error);
    return false;
  }
};

const handleLogout = () => {
  localStorage.removeItem(API_CONFIG.STORAGE_KEY);

  // Verhindere Redirect-Schleifen auf der Login-Seite
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
};

const processQueue = (error, token = null) => {
  refreshQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  refreshQueue = [];
};

// Token-Aktualisierung (singleton pattern)
const refreshToken = async () => {
  if (refreshPromise) return refreshPromise;

  const user = getUserFromStorage();
  if (!user || !user.refreshToken) {
    const error = new Error("Kein gültiger Refresh-Token vorhanden");
    return Promise.reject(error);
  }

  refreshPromise = authInstance.post('/refreshToken', {
    refreshToken: user.refreshToken
  }).then(response => {
    const newUserData = response.data;
    localStorage.setItem(API_CONFIG.STORAGE_KEY, JSON.stringify(newUserData));
    return newUserData;
  }).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

// Interceptors
const createRequestInterceptor = (axiosInstance) => {
  return axiosInstance.interceptors.request.use(
    (config) => {
      const user = getUserFromStorage();
      if (user && user.accessToken && config.headers) {
        config.headers[API_CONFIG.TOKEN_HEADER] = `Bearer ${user.accessToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

const createResponseInterceptor = (axiosInstance) => {
  return axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Keine Authentifizierungsfehler oder kein originalRequest
      if (!originalRequest || !error.response || error.response.status !== 401) {
        return Promise.reject(error);
      }

      // Verhindere Endlosschleifen bei refresh-Token-Endpunkt
      if (originalRequest.url && originalRequest.url.includes('/refreshToken')) {
        handleLogout();
        return Promise.reject(error);
      }

      // Token-Aktualisierung notwendig
      if (!originalRequest._retry) {
        originalRequest._retry = true;

        // Wenn bereits eine Aktualisierung läuft, füge Request zur Warteschlange hinzu
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshQueue.push({ resolve, reject });
          }).then(token => {
            if (originalRequest.headers) {
              originalRequest.headers[API_CONFIG.TOKEN_HEADER] = `Bearer ${token}`;
            }
            return axiosInstance(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        isRefreshing = true;

        try {
          const userData = await refreshToken();
          processQueue(null, userData.accessToken);

          if (originalRequest.headers) {
            originalRequest.headers[API_CONFIG.TOKEN_HEADER] = `Bearer ${userData.accessToken}`;
          }

          return axiosInstance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError);
          handleLogout();
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};

// Interceptors für alle Instanzen registrieren
const instances = [authInstance, bewerberInstance, jobsInstance, flyerInstance, plakatInstance, userInstance, umantisInstance, contractsInstance];

instances.forEach(instance => {
  createRequestInterceptor(instance);
  createResponseInterceptor(instance);
});

// HTTP-Hilfsfunktionen
const HttpUtils = {
  isTokenValid,
  refreshToken: async () => {
    try {
      return await refreshToken();
    } catch (error) {
      handleLogout();
      throw error;
    }
  },
  getAuthorizationHeader: () => {
    const user = getUserFromStorage();
    return user?.accessToken ? { [API_CONFIG.TOKEN_HEADER]: `Bearer ${user.accessToken}` } : {};
  },
  getUserRole: () => {
    const user = getUserFromStorage();
    return user?.role || null;
  },
  isAdmin: () => {
    const user = getUserFromStorage();
    return user?.role === "admin";
  },
  logout: handleLogout
};

export { authInstance, bewerberInstance, jobsInstance, flyerInstance, plakatInstance, userInstance, umantisInstance, contractsInstance, HttpUtils, API_CONFIG };
